import * as AudioFormat from "../interfaces/IAudioFormat";
import { Float32ToInt16SamplesConverter } from "./utils";
import { LogEvents } from "../../utils/LogEvents";
import { WavFormatKeys } from "./telemetry/WavFormatKeys";
import { AudioBackupMethodStates, AudioBackupLogError } from "./telemetry/ErrorHelper";

export class WavFormat implements AudioFormat.IAudioFormat {
    private props: AudioFormat.IFormatProperties|null = null;

    public InitializeEncoder(props: AudioFormat.IFormatProperties): void {
        LogEvents.WavFormat.AddData([{ key: WavFormatKeys.Initialize, value: AudioBackupMethodStates.called}]);
        if (props.format != AudioFormat.SupportedFormats.wav)
            throw AudioBackupLogError(LogEvents.WavFormat, WavFormatKeys.Initialize, "Unsupported format: " + props.format);

        if (props.countOfChannels != 1)
            throw AudioBackupLogError(LogEvents.WavFormat, WavFormatKeys.Initialize, "Only 1 channel supported");

        if (props.bitsPerSample != 16)
            throw AudioBackupLogError(LogEvents.WavFormat, WavFormatKeys.Initialize, "Only 16 bitsPerSample supported");

        if (props.sampleRateHz <= 0)
            throw AudioBackupLogError(LogEvents.WavFormat, WavFormatKeys.Initialize, "Invalid sample rate");

        this.props = props;
        LogEvents.WavFormat.AddData([{ key: WavFormatKeys.Initialize, value: AudioBackupMethodStates.success}]);
    }

    public EncodeChunk(chunk: Float32Array): ArrayBuffer {
        LogEvents.WavFormat.AddData([{ key: WavFormatKeys.EncodeChunk, value: AudioBackupMethodStates.success}]);
        return Float32ToInt16SamplesConverter(chunk);
    }

    public async EncodeFileUsingChunks(fileName: string, chunks: ArrayBuffer[]): Promise<AudioFormat.IAudioFile> {
        // assumes that each sample has already been converted from float64 to 16bit unsigned
        LogEvents.WavFormat.AddData([{ key: WavFormatKeys.EncodeFile, value: AudioBackupMethodStates.called}]);
        const countBytes: number = chunks.reduce<number>((previousValue: number, chunk: ArrayBuffer): number => {
            return (previousValue + chunk.byteLength);
            }, 0 /*initialValue*/);

        if (countBytes == 0) {
            throw AudioBackupLogError(LogEvents.WavFormat, WavFormatKeys.EncodeFile, "No audio data to encode");
        }

        /* There are 44 header bytes for this wav file that we will generate first */
        const buffer: ArrayBuffer = new ArrayBuffer(44 + (countBytes));
        const view: DataView = new DataView(buffer);

        this.WriteWavHeader(view, countBytes);
        this.WriteChunksToViewFromOffset(chunks, view, 44 /*offset*/);

        const options: FilePropertyBag = {
            lastModified: Date.now(),
            type: this.props!.format
        };

        const file: File = new File([view], fileName + '.wav', options);
        const duration: number = this.CalculateAudioDurationInSeconds(countBytes);

        LogEvents.WavFormat.AddData([{ key: WavFormatKeys.EncodeFile, value: AudioBackupMethodStates.success}]);
        return {file, duration};
    }

    private WriteString(view: DataView, offset: number, string: string): void {
        for (let i: number = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }

    private WriteChunksToViewFromOffset(chunks: ArrayBuffer[], view: DataView, offset: number): void {
        chunks.map((chunk: ArrayBuffer): void => {
            const samples: Uint16Array = new Uint16Array(chunk);
            samples.map((sample: number) : number => {
                view.setInt16(offset, sample, true);
                offset+=2;
                return 0;
            });
        });
    }

    private WriteWavHeader(view: DataView, countBytes: number): void {
        /* block align (channel count * bytes per sample) */
        const blockAlign: number = this.props!.countOfChannels * 2;

        /* RIFF identifier */
        this.WriteString(view, 0, 'RIFF');
        /* RIFF chunk length */
        view.setUint32(4, 36 + countBytes, true);
        /* RIFF type */
        this.WriteString(view, 8, 'WAVE');
        /* format chunk identifier */
        this.WriteString(view, 12, 'fmt ');
        /* format chunk length */
        view.setUint32(16, 16, true);
        /* sample format (raw) */
        view.setUint16(20, 1, true);
        /* channel count */
        view.setUint16(22, this.props!.countOfChannels, true);
        /* sample rate */
        view.setUint32(24, this.props!.sampleRateHz, true);
        /* byte rate (sample rate * block align) */
        view.setUint32(28, this.props!.sampleRateHz * blockAlign, true);
        /* block align (channel count * bytes per sample) */
        view.setUint16(32, blockAlign, true);
        /* bits per sample */
        view.setUint16(34, this.props!.bitsPerSample, true);
        /* data chunk identifier */
        this.WriteString(view, 36, 'data');
        /* data chunk length */
        view.setUint32(40, countBytes, true);
    }

    private CalculateAudioDurationInSeconds(countBytes: number): number {
        const bytesPerSample = this.props!.bitsPerSample/8;
        return (countBytes/(bytesPerSample * this.props!.sampleRateHz));
    }
};